// @flow

import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useToasts } from 'react-toast-notifications'
import { BtnListMobileNew } from '@bfg/components/utils/ButtonsList'
import { usePageSizeExperiment } from '@bfg/contexts/ExperimentsContext'

const useDragNDrop = (handleDropFile) => {

  const [dragging, setDragging] = useState(false)

  const dropRef = useRef()

  let dragCounter = 0

  useEffect(() => {
    const fileCheck = (files) => {
      return files && files.length === 1 && ['image/png', 'image/jpeg', // 'image/heic',
        // 'image/heic-sequence',
        // 'image/heif',
        // 'image/heif-sequence'
      ].includes(files[0].type)
    }

    const preventDefaults = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const handleDrag = (e: DragEvent) => {
      preventDefaults(e)
    }

    const handleDrop = (e: DragEvent) => {
      preventDefaults(e)
      setDragging(false)
      if (fileCheck(e.dataTransfer?.files)) {
        handleDropFile(e.dataTransfer?.files[0])
        dragCounter = 0
      }
    }

    const handleDragIn = (e: DragEvent) => {
      e.preventDefault()
      dragCounter++
      if (fileCheck(e.dataTransfer?.items)) {
        setDragging(true)
      }
    }

    const handleDragOut = (e: DragEvent) => {
      e.preventDefault()
      dragCounter--
      if (dragCounter === 0) {
        setDragging(false)
      }
    }

    const div = dropRef.current
    div?.addEventListener('dragenter', handleDragIn)
    div?.addEventListener('dragleave', handleDragOut)
    div?.addEventListener('dragover', handleDrag)
    div?.addEventListener('drop', handleDrop)
    return () => {
      div?.removeEventListener('dragenter', handleDragIn)
      div?.removeEventListener('dragleave', handleDragOut)
      div?.removeEventListener('dragover', handleDrag)
      div?.removeEventListener('drop', handleDrop)
    }
  })

  return { dragging, dropRef }
}

export const DragNDrop = (props: any) => {
  const { addToast } = useToasts()
  const [isLoaded, setIsLoaded] = useState(false)
  const resizePhoto = (e) => {
    const originalFile = e.target.files[0]
    const maxWidth = 1024
    const maxHeight = 1024
    const img = document.createElement('img')
    const canvas = document.createElement('canvas')
    const reader = new FileReader()
    reader.onload = function (e) {
      img.src = e.target.result
      img.onload = function () {
        if (img.width < maxWidth && img.height < maxHeight) {
          props.handleUpload(originalFile)
          return
        }
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height)
        const width = Math.round(img.width * ratio)
        const height = Math.round(img.height * ratio)
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(function (blob) {
          const fileName = originalFile.name.split('.').slice(0, -1).join('.')
          const resizedFile = new File([blob], 'resized_' + fileName + '.jpg', // originalFile
          )
          props.handleUpload(resizedFile)
        }, 'image/jpeg', 0.95)
      }
    }
    if (originalFile instanceof Blob) {
      reader.readAsDataURL(originalFile)
    } else {
      addToast('Some error with photo', {
        appearance: 'warning', autoDismiss: false,
      })
    }
  }
  const { t } = useTranslation()
  const dragNDrop = useDragNDrop(props.handleDrop)
  const classes = ['drop-area']
  if (props.image !== null && typeof props.image !== 'undefined') {
    classes.push('loaded')
  }

  useEffect(() => {
    if (props.image !== null && typeof props.image !== 'undefined') {
      setIsLoaded(true)
    } else {
      setIsLoaded(false)
    }
  }, [props.image])

  function uploadImage (e) {
    e.stopPropagation()
    const inputUpload = document.querySelector('#fileElem')
    inputUpload.click()
    e.target.blur()
  }

  const pageSizeExperiment = usePageSizeExperiment()

  return (<div className="drag-drop-box">
      <div id="drop-area" className={classes.join(' ')} ref={dragNDrop.dropRef}>


        <form id="drag-drop-form" className="drag-drop-form">

          <div className="upload-image">
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible"></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
            <div className="upload-image__invisible" onClick={uploadImage}></div>
          </div>

          <div className="drag-drop-form-inner-wrap">
            <div className="drag-drop-form-inner-box">
              <span className="bm_icon_upload-to-cloud"/>
              <p className="drag-drop-desc">{t('parent.dnd.Drag & drop your photo')}</p>
              <p className="drag-drop-desc">{t('parent.dnd.or')}</p>
              <p className="drag-drop-desc mobile">{props.mobileUploadPlaceholder}</p>
              <input
                type="file"
                id="fileElem"
                // accept="image/png, image/jpeg, image/heic, image/heif, image/heif-sequence, image/heic-sequence"
                accept="image/png, image/jpeg"
                onChange={(e) => {
                  resizePhoto(e)
                }}
              />
              <label htmlFor="fileElem"
                     onClick={(e) => {
                       e.stopPropagation()
                     }}
                     className={`btn ${isLoaded ? '' : 'btn-file-elem'}`}
              >{t('parent.dnd.Upload Image')}</label>
            </div>
          </div>
          <button id="btn-remove"
                  type="reset"
                  className="btn-remove"
                  title={t('parent.dnd.Remove photo')}>{t('parent.dnd.Remove photo')}
          </button>
        </form>
        {props.uploading && (<ProgressBar {...{ value: props.uploadPercent }}/>)}
        <div id="drag-drop-gallery"
             className="drag-drop-gallery centered-img">
          {props.image !== null && typeof props.image !== 'undefined' && (<img src={props.image}/>)}
        </div>
        <p className="drag-drop-note">{t('parent.dnd.Suppots JPG, JPG200, PNG and max image size of 8MB')}</p>
      </div>
    </div>)
}

const ProgressBar = (props: any) => {
  return (<progress id="progress-bar"
                    max={100}
                    value={props.value}/>)
}

export const DragNDropNew = (props: any) => {

  const resizePhoto = (e) => {
    const originalFile = e.target.files[0]
    const maxWidth = 1024
    const maxHeight = 1024
    const img = document.createElement('img')
    const canvas = document.createElement('canvas')
    const reader = new FileReader()
    reader.onload = function (e) {
      img.src = e.target.result
      img.onload = function () {
        if (img.width < maxWidth && img.height < maxHeight) {
          props.handleUpload(originalFile)
          return
        }
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height)
        const width = Math.round(img.width * ratio)
        const height = Math.round(img.height * ratio)
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(function (blob) {
          const fileName = originalFile.name.split('.').slice(0, -1).join('.')
          const resizedFile = new File([blob], 'resized_' + fileName + '.jpg', // originalFile
          )
          props.handleUpload(resizedFile)
        }, 'image/jpeg', 0.95)
      }
    }
    reader.readAsDataURL(originalFile)
  }
  const inputRef = useRef()
  const celebButtonRef = useRef()
  const { t } = useTranslation()
  const dragNDrop = useDragNDrop(props.handleDrop)
  const classes = ['drop-area']
  if (props.image !== null && typeof props.image !== 'undefined') {
    classes.push('loaded')
  }
  return (<div className="drag-drop-box"
               onClick={(e) => {
                 if (!e.target.classList.contains('popup-overlay') && !e.target.classList.contains('popup-btn') && !e.target.classList.contains('celeb-use') && !e.target.classList.contains('upload-button-super')) {
                   inputRef.current.focus()
                   inputRef.current.click()
                 }
               }}>
      <div id="drop-area" className={classes.join(' ')} ref={dragNDrop.dropRef}>
        <form id="drag-drop-form" className="drag-drop-form">
          <div className="drag-drop-form-inner-wrap new">
            <div className="drag-drop-form-inner-box new">
              <span className="bm_icon_upload-to-cloud"/>
              <p className="drag-drop-desc">{t('parent.dnd.Drag & drop your photo')}</p>
              <p className="drag-drop-desc">{t('parent.dnd.or')}</p>
              <p className="drag-drop-desc mobile">{props.mobileUploadPlaceholder}</p>
              <input
                type="file"
                id="fileElem"
                // accept="image/png, image/jpeg, image/heic, image/heif, image/heif-sequence, image/heic-sequence"
                ref={inputRef}
                accept="image/png, image/jpeg"
                onChange={(e) => {
                  resizePhoto(e)

                }}
              />
              <label htmlFor="fileElem"
                     className="btn upload-button-super new"
                     style={{ marginBottom: `20px` }}
              >{t('parent.dnd.Upload Image')}</label>
              <BtnListMobileNew
                openPopup={props.openPopup}
                parent={props.parent}
              />
              {/* <div className="btn-list mobile"> */}

              {/*  <div className={`single-btn-box full-width`}>*/}
              {/* <a
                  href="#choose-celebrity-form"
                  className="btn popup-with-form celeb-use btn-list mobile"
                  title={`...${t('parent.buttons.OR CHOOSE A CELEBRITY')}`}
                  onClick={props.openPopup}
                  ref={celebButtonRef}
                  style={{marginBottom: `20px`}}
                >{`...${t('parent.buttons.OR CHOOSE A CELEBRITY')}`}</a> */}
              {/*</div>*/}
              {/* </div> */}
            </div>
          </div>
          <button id="btn-remove"
                  type="reset"
                  className="btn-remove"
                  title={t('parent.dnd.Remove photo')}>{t('parent.dnd.Remove photo')}
          </button>
        </form>
        {props.uploading && (<ProgressBar {...{ value: props.uploadPercent }}/>)}
        <div id="drag-drop-gallery"
             className="drag-drop-gallery centered-img">
          {props.image !== null && typeof props.image !== 'undefined' && (<img src={props.image}/>)}
        </div>
        {/* <p className="drag-drop-note">{t('parent.dnd.Suppots JPG, JPG200, PNG and max image size of 8MB')}</p> */}
      </div>
    </div>)
}
